"use client";

import { useState } from "react";
import { DollarSign, Shield, Rocket, Settings, TrendingUp, HelpCircle } from "lucide-react";
import { LucideIcon } from "lucide-react";
import { useT } from "@/hooks/useT";

interface FaqItem {
  question: string;
  answer: string;
}

interface FaqSection {
  title: string;
  icon: LucideIcon;
  faqs: FaqItem[];
}

export default function FAQSection() {
  const t = useT();
  const [open, setOpen] = useState<string | null>(null);

  const faqSections: FaqSection[] = [
    {
      title: t("faq.section.bot"),
      icon: TrendingUp,
      faqs: [
        { question: t("faq.bot.q1"), answer: t("faq.bot.a1") },
        { question: t("faq.bot.q2"), answer: t("faq.bot.a2") },
        { question: t("faq.bot.q3"), answer: t("faq.bot.a3") },
      ],
    },
    {
      title: t("faq.section.markets"),
      icon: HelpCircle,
      faqs: [
        { question: t("faq.markets.q1"), answer: t("faq.markets.a1") },
        { question: t("faq.markets.q2"), answer: t("faq.markets.a2") },
        { question: t("faq.markets.q3"), answer: t("faq.markets.a3") },
      ],
    },
    {
      title: t("faq.section.settings"),
      icon: Settings,
      faqs: [
        { question: t("faq.settings.q1"), answer: t("faq.settings.a1") },
        { question: t("faq.settings.q2"), answer: t("faq.settings.a2") },
        { question: t("faq.settings.q3"), answer: t("faq.settings.a3") },
      ],
    },
    {
      title: t("faq.section.fees"),
      icon: DollarSign,
      faqs: [
        { question: t("faq.fees.q1"), answer: t("faq.fees.a1") },
        { question: t("faq.fees.q2"), answer: t("faq.fees.a2") },
        { question: t("faq.fees.q3"), answer: t("faq.fees.a3") },
      ],
    },
    {
      title: t("faq.section.security"),
      icon: Shield,
      faqs: [
        { question: t("faq.security.q1"), answer: t("faq.security.a1") },
        { question: t("faq.security.q2"), answer: t("faq.security.a2") },
        { question: t("faq.security.q3"), answer: t("faq.security.a3") },
      ],
    },
    {
      title: t("faq.section.start"),
      icon: Rocket,
      faqs: [
        { question: t("faq.start.q1"), answer: t("faq.start.a1") },
        { question: t("faq.start.q2"), answer: t("faq.start.a2") },
        { question: t("faq.start.q3"), answer: t("faq.start.a3") },
      ],
    },
  ];

  return (
    <section className="px-4 py-12 sm:px-6 sm:py-20 lg:px-24 bg-white dark:bg-[#0f0f12]">
      <div className="max-w-7xl mx-auto mb-8 sm:mb-16 text-center">
        <p className="text-sm font-monument font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
          FAQ
        </p>
        <h2 className="text-2xl sm:text-4xl md:text-5xl font-monument font-bold text-gray-900 dark:text-white mb-4">
          {t("faq.headline.before")}{" "}
          <span className="text-accent-primary">FAQ</span>
        </h2>
        <p className="text-lg font-satoshi text-gray-500 dark:text-gray-400">
          {t("faq.subtitle")}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-12 max-w-7xl mx-auto [&>*]:min-h-0">
        {faqSections.map((section, sectionIndex) => {
          const IconComponent = section.icon;
          return (
            <div key={sectionIndex} className="flex flex-col min-h-0 gap-3 sm:gap-6">
              <div className="flex items-center gap-3">
                <div className="bg-gray-100 dark:bg-[#18181D] p-3 rounded-[5px] w-12 h-12 flex items-center justify-center">
                  <IconComponent className="w-6 h-6 text-gray-600 dark:text-gray-400" />
                </div>
                <h3 className="text-xl font-monument font-semibold text-gray-900 dark:text-white">
                  {section.title}
                </h3>
              </div>

              <div className="flex flex-col gap-3">
                {section.faqs.map((faq, faqIndex) => {
                  const id = `${sectionIndex}-${faqIndex}`;
                  const isOpen = open === id;

                  return (
                    <div
                      key={id}
                      className="border border-gray-200 dark:border-[#1A1A1A] rounded-[5px] bg-white dark:bg-[#18181D] overflow-hidden"
                    >
                      <button
                        onClick={() => setOpen(isOpen ? null : id)}
                        className="flex w-full items-center justify-between px-4 py-3 sm:px-6 sm:py-4 text-left font-monument font-medium text-sm sm:text-base text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-[#1e1e24] transition-colors"
                      >
                        <span>{faq.question}</span>
                        <span className="text-2xl text-gray-400 dark:text-gray-500 font-bold ml-4 flex-shrink-0">
                          {isOpen ? "\u2212" : "+"}
                        </span>
                      </button>
                      {isOpen && (
                        <div className="px-4 sm:px-6 pb-4 text-sm font-satoshi text-gray-600 dark:text-gray-400">
                          {faq.answer}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
